import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './UserManagementRedesign.css';

const regions = [
  '곡성읍', '오곡면', '삼기면', '석곡면', '목사동면',
  '죽곡면', '고달면', '옥과면', '입면', '겸면', '오산면'
];

const deviceTypes = ['plug', 'switch', 'sensor'];

const statusClass = (status) =>
  status === '정상'
    ? 'um-status um-normal'
    : status === '비정상'
      ? 'um-status um-abnormal'
      : 'um-status um-missing';

const parseJwt = (token) => {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(
      decodeURIComponent(
        window.atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
    );
  } catch {
    return null;
  }
};

const getRegion = (departmentName) => {
  const department = String(departmentName || '').trim();
  return regions.find(region => department === `${region}사무소`) || '';
};

export default function UserManagementRedesign({ mode = 'alluser', onChangeTab }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    sex: '전체',
    risk: '전체',
    device: '전체',
    query: ''
  });

  const regionFromUrl = searchParams.get('region') || '';
  const selectedRegion = getRegion(regionFromUrl) || '전체';

  const pageFromUrl = Number(searchParams.get('page'));
  const page =
    Number.isInteger(pageFromUrl) && pageFromUrl > 0
      ? pageFromUrl
      : 1;

  const pageSize = 10;

  const token = localStorage.getItem('token');
  const currentUser = token ? parseJwt(token) : null;
  const isSuperAdmin = Number(currentUser?.auth) === 1;

  useEffect(() => {
    let active = true;

    const fetchUsers = async () => {
      setLoading(true);
      setError('');

      try {
        const endpoint =
          mode === 'selectuser' ? '/user/manager' : '/user';

        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (!active) return;

        if (!Array.isArray(response.data.user)) {
          throw new Error('사용자 목록 응답 형식이 올바르지 않습니다.');
        }

        setUsers(response.data.user);
      } catch (err) {
        if (!active) return;
        console.error('사용자 조회 실패:', err);
        setError('사용자 목록을 불러오지 못했습니다.');
        setUsers([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchUsers();

    return () => {
      active = false;
    };
  }, [mode]);

  const updateUrl = (changes) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === '') next.delete(key);
      else next.set(key, String(value));
    });

    setSearchParams(next);
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    updateUrl({ page: '1' });
  };

  const changeRegion = (region) => {
    updateUrl({
      region: region === '전체' ? null : `${region}사무소`,
      page: '1'
    });
  };

  const resetFilters = () => {
    setFilters({
      sex: '전체',
      risk: '전체',
      device: '전체',
      query: ''
    });

    // 대시보드에서 전달된 읍면 필터는 유지
    updateUrl({ page: '1' });
  };

  const normalizedUsers = useMemo(() => users.map(user => ({
    ...user,
    region: getRegion(user.department_name),
    risk: user.sev_name || '미분류',
    guardian: '-',
	  plug: Number(user.has_plug) === 1
  ? (Number(user.plug_status) === 1 ? '정상' : '비정상')
  : '미설치',

switch: Number(user.has_switch) === 1
  ? (Number(user.swich_status) === 1 ? '정상' : '비정상')
  : '미설치',
	  sensor: Number(user.has_motion) === 1
  ? (user.motion_status == null
      ? '확인 필요'
      : Number(user.motion_status) === 1
        ? '정상'
        : '비정상')
  : '미설치',

latestAlertLevel: user.latest_alert_level == null
  ? '알림 없음'
  : `${user.latest_alert_level}차`,
latestAlertAt: user.latest_alert_at || '-'
  })), [users]);

  const filtered = useMemo(() => normalizedUsers.filter(user => {
    if (regionFromUrl &&
        String(user.department_name || '').trim() !== regionFromUrl) {
      return false;
    }

    if (filters.sex !== '전체' && user.sex !== filters.sex) {
      return false;
    }

    if (filters.risk !== '전체' && user.risk !== filters.risk) {
      return false;
    }

    // 사용자-장비 ID 연결 전에는 장비 상태 필터를 적용하지 않음
	  // // 플러그·스위치·감지기 중 하나라도 선택한 상태와 일치하면 표시
if (filters.device !== '전체') {
  const deviceStatuses = [user.plug, user.switch, user.sensor];

  if (!deviceStatuses.includes(filters.device)) {
    return false;
  }
}

    const query = filters.query.trim().toLowerCase();

    if (!query) return true;

    return [
      user.name,
      user.address,
      user.detail_address,
      user.age,
      user.sex,
      user.sev_name,
      user.department_name,
      user.pec
    ].some(value =>
      String(value ?? '').toLowerCase().includes(query)
    );
  }), [normalizedUsers, regionFromUrl, filters]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const changePage = (nextPage) => {
    updateUrl({ page: String(nextPage) });
  };

  const selectOptions = (key, label, options, disabled = false) => (
    <label className="um-filter" key={key}>
      <span>{label}</span>
      <select
        disabled={disabled}
        value={filters[key]}
        onChange={event => updateFilter(key, event.target.value)}
      >
        {['전체', ...options].map(option => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </label>
  );

  // 페이지 번호를 5개씩 고정해서 표시
  const pageGroupSize = 5;

  const startPage =
    Math.floor((currentPage - 1) / pageGroupSize) * pageGroupSize + 1;

  const endPage = Math.min(startPage + pageGroupSize - 1, pages);

  const pageNumbers = Array.from(
    { length: endPage - startPage + 1 },
    (_, index) => startPage + index
  );

  return (
    <div className="um-page">
      <div className="um-heading">
        <div>
          <h2>
            {mode === 'selectuser' ? '담당 사용자 관리' : '전체 사용자 관리'}
          </h2>
          <p>어르신 정보 및 가정별 장비 현황</p>
        </div>

        <span className="um-total">전체 {users.length}명</span>
      </div>
      <div className="um-tabs">
        <button
          type="button"
          className={mode === 'alluser' ? 'active' : ''}
          onClick={() => onChangeTab?.('alluser')}
        >
          전체 사용자
        </button>

        <button
          type="button"
          className={mode === 'selectuser' ? 'active' : ''}
          onClick={() => onChangeTab?.('selectuser')}
        >
          담당 사용자
        </button>
      </div>
      <div className="um-panel">
        <div className="um-filters">
          <label className="um-filter">
            <span>읍면별</span>
            <select
              value={selectedRegion}
              onChange={event => changeRegion(event.target.value)}
            >
              <option value="전체">전체</option>
              {regions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
          </label>

          {selectOptions('sex', '성별', ['남성', '여성'])}
          {selectOptions('risk', '위험등급',
            ['고위험군', '중위험군', '저위험군'])}
          {selectOptions('device', '장비 상태', ['정상', '비정상', '미설치'])}
        </div>

        <div className="um-search">
          <input
            value={filters.query}
            onChange={event => updateFilter('query', event.target.value)}
            placeholder="어르신 이름 또는 주소 검색"
          />
          <button type="button" onClick={resetFilters}>
            초기화
          </button>
        </div>

        <div className="um-table-top">
          <strong>조회 결과 {filtered.length}명</strong>
          <span>
            {isSuperAdmin && (
              <button
                type="button"
                onClick={() => navigate('/user/new')}
              >
                사용자 등록
              </button>
            )}
            {' '}행을 선택하면 상세 정보를 볼 수 있습니다
          </span>
        </div>

        <div className="um-table-wrap">
          <table className="um-table">
            <thead>
              <tr>
                <th>No</th>
                <th>이름</th>
                <th>성별 / 나이</th>
                <th>주소</th>
                <th>위험등급</th>
                <th>보호자</th>
                <th>플러그</th>
                <th>스위치</th>
                <th>감지기</th>
	  	<th>최근 알림 단계</th>
		<th>최근 알림 시간</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="um-empty">
                    사용자 목록을 불러오는 중입니다.
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="11" className="um-empty">{error}</td>
                </tr>
              ) : visible.length ? (
                visible.map((user, index) => (
                  <tr
                    key={user.id}
                    tabIndex={0}
                    onClick={() => navigate(`/user/${user.id}`)}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        navigate(`/user/${user.id}`);
                      }
                    }}
                  >
                    <td>
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="um-name">{user.name}</td>
                    <td>
                      {user.sex || '-'} / {user.age ?? '-'}세
                    </td>
                    <td className="um-address">
                      {[user.address, user.detail_address]
                        .filter(Boolean).join(' ') || '-'}
                    </td>
                    <td>
                      <span className={
                        'um-risk ' +
                        (user.risk === '고위험군'
                          ? 'um-high'
                          : user.risk === '중위험군'
                            ? 'um-medium'
                            : 'um-low')
                      }>
                        {user.risk}
                      </span>
                    </td>
                    <td>{user.guardian}</td>
                    {deviceTypes.map(type => (
                      <td key={type}>
                        <span className={statusClass(user[type])}>
                          {user[type]}
                        </span>
                      </td>
                    ))}
			<td>{user.latestAlertLevel}</td>
			<td className="um-time">{user.latestAlertAt}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="um-empty">
                    조건에 맞는 사용자가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="um-bottom">
          <span>
            {filtered.length === 0
              ? 0
              : (currentPage - 1) * pageSize + 1}
            –
            {Math.min(currentPage * pageSize, filtered.length)}
            {' / '}{filtered.length}명
          </span>

          <div className="um-pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => changePage(currentPage - 1)}
            >
              이전
            </button>

            {pageNumbers.map(number => (
              <button
                key={number}
                className={number === currentPage ? 'um-current' : ''}
                onClick={() => changePage(number)}
              >
                {number}
              </button>
            ))}

            <button
              disabled={currentPage === pages}
              onClick={() => changePage(currentPage + 1)}
            >
              다음
            </button>
          </div>
        </div>
      </div>

      <p className="um-note">
        ※ 보호자·장비 상태·최근 활동 정보는 연동 확인 전까지
        표시를 보류합니다. 실제 사용자 정보는 서버 API를 사용합니다.
      </p>
    </div>
  );
}
